// Privacy & Disclosures e-sign: read the full document, then each applicant draws their signature.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, CheckCircle2, Loader2, ShieldCheck, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDateTime } from './rules';
import SignaturePad from '../SignaturePad';

const fileToDataUrl = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

export default function DisclosuresStep({ payload, api, onBack, onDone }) {
  const docDef = payload.disclosure_doc || { title: 'Privacy & Disclosures', sections: [], version: '' };
  const signApplicants = payload.sign_applicants || [];
  const already = payload.signed_disclosure;
  const [reviewed, setReviewed] = useState(false);
  const [sigs, setSigs] = useState({});               // applicant_id -> dataURL
  const [busy, setBusy] = useState(false);
  const signRef = useRef(null);
  const allSigned = useMemo(() => signApplicants.length > 0 && signApplicants.every((a) => sigs[a.id]), [signApplicants, sigs]);

  useEffect(() => {
    if (reviewed && signRef.current) {
      const t = setTimeout(() => signRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
      return () => clearTimeout(t);
    }
  }, [reviewed]);

  const onSaved = async (applicantId, file) => {
    try {
      const dataUrl = await fileToDataUrl(file);
      setSigs((s) => ({ ...s, [applicantId]: dataUrl }));
    } catch { toast.error('Could not capture signature'); }
  };
  const onCleared = (applicantId) => setSigs((s) => { const n = { ...s }; delete n[applicantId]; return n; });

  const submit = async () => {
    setBusy(true);
    try {
      await api.signDisclosures(signApplicants.map((a) => ({ applicant_id: a.id, signature: sigs[a.id] })));
      toast.success('Thank you — your signed document has been saved');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save — please try again'); } finally { setBusy(false); }
  };

  return (
    <div data-testid="ob-disclosures" className="pb-28 sm:pb-0">
      <button type="button" onClick={onBack} data-testid="ob-section-back" className="inline-flex items-center gap-1.5 rounded-full bg-[#F4F4F6] px-3 py-2 text-sm font-semibold text-slate-600 hover:text-[#2E333B] mb-5 min-h-[40px]"><ArrowLeft className="h-4 w-4" /> Overview</button>
      <h1 className="text-[22px] leading-[1.15] sm:text-4xl font-extrabold text-[#2E333B] tracking-tight font-[Outfit]">{docDef.title}</h1>
      <p className="mt-1.5 text-[13px] sm:text-[15px] text-slate-600 leading-relaxed">{docDef.intro || 'Please read this document in full, then sign below.'}</p>
      {docDef.version && <p className="mt-1 text-[11px] sm:text-xs text-slate-500">Document version <span className="font-mono font-semibold">{docDef.version}</span></p>}

      {already ? (
        <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="ob-disclosures-signed">
          <div className="flex items-center gap-2.5"><CheckCircle2 className="h-5 w-5 text-emerald-600" /><span className="font-bold text-emerald-900">Signed & saved</span></div>
          <ul className="mt-2 space-y-0.5 text-sm text-emerald-800">
            {(already.signers || []).map((s, i) => <li key={i}>{s.name} — {fmtDateTime(s.signed_at)}</li>)}
          </ul>
        </div>
      ) : (<>
        {/* Full document */}
        <div className="mt-4 sm:mt-6 rounded-2xl border border-slate-200 bg-white" data-testid="ob-disclosure-doc">
          <div className="max-h-[30vh] sm:max-h-[52vh] overflow-auto px-4 py-3.5 sm:px-6 sm:py-5 space-y-4 sm:space-y-5">
            {docDef.sections.map((sec, i) => (
              <div key={i}>
                <h3 className="text-[13px] sm:text-sm font-black uppercase tracking-wide text-[#2E333B]">{sec.heading}</h3>
                <p className="mt-1.5 whitespace-pre-line text-[13px] sm:text-[13.5px] leading-relaxed text-slate-600">{sec.body}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 px-4 py-3 sm:px-6">
            <label className={`flex items-start gap-3 cursor-pointer rounded-xl border px-3.5 py-3 transition-colors ${reviewed ? 'border-[#2E333B] bg-[#2E333B]/5' : 'border-slate-200 bg-white'}`}>
              <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} data-testid="ob-disclosure-reviewed" className="mt-0.5 h-5 w-5 rounded accent-[#2E333B] shrink-0" />
              <span className="text-sm font-semibold text-[#2E333B] leading-snug">I have read and reviewed this consent form.</span>
            </label>
          </div>
        </div>

        {/* Signing panels */}
        {reviewed && (
          <div ref={signRef} className="mt-5 space-y-3 scroll-mt-4" data-testid="ob-sign-panels">
            {signApplicants.map((ap, idx) => {
              const done = !!sigs[ap.id];
              return (
                <div key={ap.id} data-testid={`ob-sign-${ap.id}`} className={`rounded-2xl border p-4 transition-colors ${done ? 'border-emerald-300 bg-emerald-50/40' : 'border-[#2E333B] bg-white shadow-sm'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">Applicant {idx + 1}</p>
                      <p className="font-bold text-[#2E333B]" data-testid={`ob-sign-name-${ap.id}`}>{ap.name}</p>
                    </div>
                    {done && <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-800"><CheckCircle2 className="h-3.5 w-3.5" /> Signed</span>}
                  </div>
                  <div className="mt-3">
                    <p className="mb-2 text-xs font-semibold text-slate-500 flex items-center gap-1.5"><PenLine className="h-3.5 w-3.5" /> Sign here — the date is added automatically</p>
                    <SignaturePad onSaved={(file) => onSaved(ap.id, file)} onCleared={() => onCleared(ap.id)} savedAt={done} testIdPrefix={`ob-sig-${ap.id}`} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>)}

      {!already && (
        <div className="mt-6 fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 backdrop-blur px-3 pt-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] sm:static sm:border-t sm:border-slate-100 sm:bg-transparent sm:px-0 sm:pt-6 sm:pb-0 flex items-center gap-2 sm:justify-end" data-testid="ob-sign-bar">
          <button type="button" onClick={onBack} aria-label="Back" className="sm:hidden inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-[#2E333B]"><ArrowLeft className="h-5 w-5" /></button>
          {!allSigned && <span className="sm:hidden flex-1 min-w-0 text-[11px] leading-tight text-slate-500">{!reviewed ? 'Review, then sign to continue' : 'Each applicant signs to continue'}</span>}
          <button type="button" disabled={!allSigned || busy} onClick={submit} data-testid="ob-sign-submit"
            className={`${allSigned ? 'flex-1' : 'shrink-0'} sm:flex-none inline-flex items-center justify-center gap-2 rounded-2xl bg-[#86EFAC] px-4 sm:px-6 h-12 sm:h-[52px] font-bold text-[#14532D] disabled:opacity-50 active:scale-[0.98] transition whitespace-nowrap`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} <span className="sm:hidden">{allSigned ? 'Finish & save signed document' : 'Finish & sign'}</span><span className="hidden sm:inline">Finish & save signed document</span></button>
        </div>
      )}
    </div>
  );
}
